<script>
    export let invoice = null;
    // Free trials have no Stripe invoice to read from (nothing is due now), so the
    // shop passes the trial details it already resolved for the product page.
    export let trial = null;

    const currencies = { 'usd': '$', 'cad': 'CA$', 'eur': '€', 'gbp': '£' };

    let total = 0;
    let showTotal = false;
    let taxAmount = 0
    let productTotal = (invoice?.total || 0) / 100;
    let subTotal = 0;

    let priceItems = [];
    let trialInfo = null;

    function formatTrialDuration(days) {
        if (!days) return '';
        const months = Math.round(days / 30);
        if (months >= 1 && days % 30 <= 5) return `${months} month`;
        return `${days} day`;
    }

    function isTruthyMeta(value) {
        return String(value).toLowerCase() === 'true';
    }

    function trialInfoFromProp() {
        if (!trial || !trial.days) return null;
        return {
            days: trial.days,
            autoRenew: !!trial.autoRenew,
            renewalPrice: trial.renewalPrice != null ? trial.renewalPrice : null,
            renewalCurrency: trial.renewalCurrency || '$',
            interval: trial.interval || 'month',
        };
    }

    function extractTrialInfo(invoiceData) {
        const meta = invoiceData?.subscription_details?.metadata
            || invoiceData?.parent?.subscription_details?.metadata;
        if (!meta || !isTruthyMeta(meta.is_trial)) return trialInfoFromProp();

        const subLine = (invoiceData?.lines?.data || []).find(l => l.type === 'subscription');
        const renewalAmount = subLine?.price?.unit_amount;
        const renewalCurrency = currencies[subLine?.price?.currency] || '$';
        const interval = subLine?.price?.recurring?.interval || 'month';

        return {
            days: parseInt(meta.trial_days) || trial?.days || 0,
            autoRenew: isTruthyMeta(meta.trial_auto_renew),
            renewalPrice: renewalAmount ? renewalAmount / 100 : (trial?.renewalPrice ?? null),
            renewalCurrency,
            interval,
        };
    }

    $: invoice, trial, updatePrice(invoice);

    function calculateDiscountInfo(lineItem) {
        let discountInfo = {
            hasDiscount: false,
            isFree: false,
            originalAmount: lineItem.amount,
            discountAmount: 0,
            finalAmount: lineItem.amount,
            discountPercentage: 0
        };

        // Check if there are discount_amounts (this is how Stripe structures discounts)
        if (lineItem.discount_amounts && lineItem.discount_amounts.length > 0) {
            discountInfo.hasDiscount = true;
            
            // Calculate total discount amount from discount_amounts array
            lineItem.discount_amounts.forEach(discountAmount => {
                discountInfo.discountAmount += discountAmount.amount;
            });

            // In Stripe: amount = original price, discount_amounts = discount applied
            discountInfo.originalAmount = lineItem.amount;
            discountInfo.finalAmount = lineItem.amount - discountInfo.discountAmount;
            
            // Check if it's free (100% discount) - final amount is 0 or negative
            discountInfo.isFree = discountInfo.finalAmount <= 0;
            
            // Calculate discount percentage
            if (discountInfo.originalAmount > 0) {
                discountInfo.discountPercentage = Math.round((discountInfo.discountAmount / discountInfo.originalAmount) * 100);
            }
        }

        return discountInfo;
    }

    export function updatePrice(invoiceData) {
        priceItems = []; // Reset array
        trialInfo = extractTrialInfo(invoiceData);

        if (invoiceData) {
            let shippingItem;
            let adminFeeItem;
            
            (invoiceData.lines?.data || []).forEach(lineItem => {
                if(lineItem.metadata?.hide_in_ui){
                    return;
                }
                if (lineItem.description == 'Shipping') {
                    shippingItem = lineItem;
                } else if (lineItem.description && lineItem.description.toLowerCase().includes('admin fee')) {
                    adminFeeItem = lineItem;
                } else {
                    const discountInfo = calculateDiscountInfo(lineItem);
                    const label = lineItem.type === 'invoiceitem' ? 
                        lineItem.quantity + " x " + lineItem.description : 
                        lineItem.description;

                    priceItems.push({
                        label: label,
                        value: (discountInfo.finalAmount / 100).toFixed(2),
                        originalValue: (discountInfo.originalAmount / 100).toFixed(2),
                        discountAmount: (discountInfo.discountAmount / 100).toFixed(2),
                        discountPercentage: discountInfo.discountPercentage,
                        hasDiscount: discountInfo.hasDiscount,
                        isFree: discountInfo.isFree || lineItem.amount === 0,
                        class: 'text-sm md:text-base lg:text-lg'
                    });
                }
            });
            
            // add shipping and admin fee at the end (shipping first, admin fee last)
            [shippingItem, adminFeeItem].filter(Boolean).forEach(item => {
                const info = calculateDiscountInfo(item);
                priceItems.push({
                    label: item.type === 'invoiceitem' ? item.quantity + " x " + item.description : item.description,
                    value: (info.finalAmount / 100).toFixed(2),
                    originalValue: (info.originalAmount / 100).toFixed(2),
                    discountAmount: (info.discountAmount / 100).toFixed(2),
                    discountPercentage: info.discountPercentage,
                    hasDiscount: info.hasDiscount,
                    isFree: info.isFree || item.amount === 0,
                    class: 'text-sm',
                });
            });
            
            total = (invoiceData.total || 0) / 100;
        } else {
            total = 0;
        }

        showTotal = true;
    }

</script>
<style type="text/scss">
  img {
    width: 60px;
    height: 60px;
  }

  .price {
    font-weight: bold;
    @media screen and (min-width: 1200px) {
      font-size: 18px;
    }
    @media screen and (max-width: 1200px) {
      font-size: 15px;
    }
  }

  h1 {
    @media screen and (min-width: 1200px) {
      font-size: 24px;
      line-height: 26px;
    }
    @media screen and (max-width: 1200px) {
      font-size: 18px;
      line-height: 20px;
      margin-bottom: 10px;
    }
  }

  .item {
    align-items: baseline;

    .dots {
      border-bottom: 1px dotted rgba(0, 0, 0, .5);
    }

    .free-label {
      opacity: 0.6;
      text-decoration: line-through;
      color: #666;
    }
  }

  .total {
    background: rgba(0, 0, 0, .4);
    color: white;
    border-radius: 5px;
    padding: 5px 10px;
    margin-top: 10px;
  }

  .discount-tag {
    background: #10b981;
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
    font-weight: 600;
  }

  .original-price {
    text-decoration: line-through;
    color: #999;
    font-size: 0.9em;
    margin-right: 8px;
  }

  .discounted-price {
    color: #10b981;
    font-weight: 600;
  }

  :global(.item.discount) {
    margin: 10px 0;
    background: white;
    color: #333;
    padding: 5px 10px;
    border-radius: 7px;
  }

  .trial-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #ecfdf5, #f0fdf4);
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 12px;
    justify-content: center;
  }

  .trial-icon {
    font-size: 22px;
    line-height: 1;
    flex-shrink: 0;
  }

  .trial-text {
    font-size: 13px;
    color: #166534;
    line-height: 1.4;
  }

  .trial-text strong {
    font-size: 14px;
    display: block;
    margin-bottom: 2px;
  }

  .trial-text p {
    margin: 0;
    color: #15803d;
    font-size: 12px;
  }

</style>

<div class="{$$props.class || ''}">
    {#if trialInfo && trialInfo.days}
        <div class="trial-banner">
            <div class="trial-text">
                <strong>You're receiving a {formatTrialDuration(trialInfo.days)} free trial!</strong>
                {#if trialInfo.autoRenew && trialInfo.renewalPrice}
                    <p>Your subscription will automatically continue at {trialInfo.renewalCurrency}{trialInfo.renewalPrice.toFixed(2)}/{trialInfo.interval} after the trial ends.</p>
                {:else if trialInfo.autoRenew}
                    <p>Your subscription will continue automatically after the trial period.</p>
                {:else}
                    <p>Your trial will end automatically — no commitment required.</p>
                {/if}
            </div>
        </div>
    {/if}
    <div class="value space-y-2">
        {#if priceItems.length === 0}
            <div class="item flex text-sm md:text-base lg:text-lg">
                <strong class="mr-2 font-normal text-sm text-left">
                    {trialInfo && trialInfo.days ? `${formatTrialDuration(trialInfo.days)} free trial` : 'Subscription'}
                </strong>
                <span class="flex-1 dots"></span>
                <div class="ml-2 font-semibold text-sm flex items-center">
                    <span class="discounted-price">FREE</span>
                </div>
            </div>
        {/if}
        {#each priceItems as lineItem, i}
            <div class="item flex {lineItem.class}">
                <strong class="mr-2 font-normal text-sm text-left">{lineItem.label}</strong>
                <span class="flex-1 dots"></span>
                <div class="ml-2 font-semibold text-sm flex items-center">
                    {#if lineItem.isFree}
                        <!-- Free item - show original price struck through -->
                        <span class="original-price">${lineItem.originalValue}</span>
                        <span class="discounted-price">FREE</span>
                        <span class="discount-tag">100% OFF</span>
                    {:else if lineItem.hasDiscount}
                        <!-- Partial discount - show original and new price -->
                        <span class="original-price">${lineItem.originalValue}</span>
                        <span class="discounted-price">${lineItem.value}</span>
                        <span class="discount-tag">{lineItem.discountPercentage}% OFF</span>
                    {:else}
                        <!-- No discount - show regular price -->
                        <span>${lineItem.value}</span>
                    {/if}
                </div>
            </div>
        {/each}
        <div class="total item flex">
            <strong class='mr-2'>Total Due Today</strong>
            <span class="flex-1 dots"></span>
            <span class="ml-2 price">${total.toFixed(2)} / {(invoice?.currency || 'usd').toUpperCase()}</span>
        </div>
        {#if total === 0 && trialInfo && trialInfo.autoRenew && trialInfo.renewalPrice}
            <p class="text-xs text-gray-500 text-center">
                Then {trialInfo.renewalCurrency}{trialInfo.renewalPrice.toFixed(2)}/{trialInfo.interval} when your trial ends.
            </p>
        {/if}
    </div>
</div>
